import React from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, ArrowRight, BookOpen, ClipboardCheck, Layers, Sparkles, BarChart3, Handshake } from "lucide-react";
import { Button } from "@/components/ui/button";

const STEPS = [
  { i: ClipboardCheck, t: "Initial Assessment", d: "We start with formal and informal measures to pinpoint exactly where your child is and what's holding them back. Assessments cover decoding and encoding, word recognition, fluency, phonics, spelling, morphology, phonological awareness, and vocabulary." },
  { i: Layers, t: "Custom Intervention Plan", d: "Using the assessment data, Jonna builds a sequenced plan: which phonics patterns to teach in what order, which fluency targets to chase, and what comprehension and vocabulary work to weave in." },
  { i: BookOpen, t: "Multi-Sensory Sessions", d: "Each 60-minute lesson uses sight, sound, and touch together — letter tiles, sound-tapping, kinesthetic spelling, decodable text. Pace is high. Wins come fast." },
  { i: Sparkles, t: "Firm-But-Fun Structure", d: "Predictable routines and clear expectations help kids with ADHD focus and feel safe. Games and quick movement breaks keep energy up. Real reading text keeps it meaningful." },
  { i: BarChart3, t: "Progress Monitoring", d: "We track WPM, accuracy, phonics mastery, and spelling stages over time. You'll see exactly how much ground your child has gained — no guessing." },
  { i: Handshake, t: "Parent & School Partnership", d: "Regular parent updates, classroom recommendations, and IEP-aligned goal language when it helps your child get more support at school." },
];

export default function Approach() {
  return (
    <main className="bg-cream">
      <section className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 py-20 lg:py-24" data-testid="approach-hero">
        <div className="max-w-3xl">
          <span className="text-xs uppercase tracking-[0.18em] text-terracotta font-semibold">Our Approach</span>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-ink text-balance">
            The Science of Reading, delivered with warmth.
          </h1>
          <p className="mt-6 text-lg text-ink/70 leading-relaxed">
            Jonna's approach is grounded in <span className="text-ink font-medium">structured literacy</span>,
            <span className="text-ink font-medium"> Orton-Gillingham</span>, and decades of classroom evidence.
            Kids with learning differences need a structured literacy approach.
          </p>
          <figure className="mt-8 border-l-4 border-terracotta pl-5 sm:pl-6">
            <blockquote className="text-base sm:text-lg text-ink/80 leading-relaxed italic">
              "Structured Literacy supports explicit, sequential, systematic, prescriptive, diagnostic, and cumulative
              instruction. Through Structured Literacy, teachers implement appropriate methods for all students and
              particularly necessary for students with learning differences."
            </blockquote>
            <figcaption className="mt-2 text-sm text-ink/50">— orton-gillingham.com</figcaption>
          </figure>
          <figure className="mt-6 border-l-4 border-sage-600 pl-5 sm:pl-6">
            <blockquote className="text-base sm:text-lg text-ink/80 leading-relaxed italic">
              "Orton-Gillingham is a highly structured approach that breaks reading and spelling down into smaller
              skills involving letters and sounds and then builds on these skills over time. It was the first approach
              to use explicit, direct, sequential, systematic, multi-sensory instruction to teach reading, which is
              effective for all students and essential for teaching students with dyslexia."
            </blockquote>
            <figcaption className="mt-2 text-sm text-ink/50">— orton-gillingham.com</figcaption>
          </figure>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 pb-20" data-testid="approach-steps">
        <div className="grid md:grid-cols-2 gap-5">
          {STEPS.map((s, i) => (
            <div key={s.t} className="rounded-2xl bg-white border border-ink/5 p-7 flex gap-5 hover:shadow-md transition" data-testid={`step-${i + 1}`}>
              <div className="flex-none">
                <div className="h-12 w-12 rounded-2xl bg-terracotta text-white flex items-center justify-center"><s.i size={20} /></div>
                <div className="mt-3 text-xs text-ink/40 font-mono">0{i + 1}</div>
              </div>
              <div>
                <h3 className="font-display text-xl font-semibold text-ink">{s.t}</h3>
                <p className="mt-2 text-sm text-ink/70 leading-relaxed">{s.d}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-ink text-cream py-20" data-testid="approach-tools">
        <div className="mx-auto max-w-5xl px-5 sm:px-8 lg:px-12">
          <span className="text-xs uppercase tracking-[0.18em] text-mustard font-semibold">Tools we use</span>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl font-semibold text-cream">Evidence-based assessments & curricula.</h2>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {[
              "IMSE Orton-Gillingham", "LETRS Phonics & Word Reading Survey", "REWARDS",
              "Words Their Way Spelling Inventory", "CORE Assessing Reading", "Aimsweb Plus",
              "DIBELS NEXT / Acadience", "CTTOP", "UFLI",
            ].map((t) => (
              <div key={t} className="rounded-xl bg-cream/5 border border-cream/10 px-4 py-3 text-sm flex items-center gap-2">
                <CheckCircle2 size={16} className="text-sage flex-none" /> {t}
              </div>
            ))}
          </div>
          <div className="mt-12">
            <Link to="/contact" data-testid="approach-cta">
              <Button className="rounded-full bg-cream text-ink hover:bg-white h-12 px-7 font-medium shadow-md hover:-translate-y-0.5 transition-all">
                Start your child's plan <ArrowRight size={18} className="ml-1.5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
